import { auth, db } from "../firebase";
import { useState, useEffect, useContext, createContext } from "react";
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    sendPasswordResetEmail,
    updateProfile,
    updateEmail,
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider,
    deleteUser
} from "firebase/auth";
import {
    doc,
    getDoc
} from "firebase/firestore";
import { useLanguage } from "./LanguageContext";
import { getLevel } from "../progression";

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({children}) {
    const [currentUser, setCurrentUser] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const {switchLanguage} = useLanguage();

    function signup(email, password) {
        return createUserWithEmailAndPassword(auth, email, password);
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    function resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    }

    function updateInformation(name) {
        return updateProfile(currentUser, {displayName: name});
    }

    function setEmail(email) {
        return updateEmail(currentUser, email);
    }

    function setPassword(password) {
        return updatePassword(currentUser, password);
    }

    function reauthenticate(password) {
        return reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, password));
    }

    function deleteAccount() {
        return deleteUser(currentUser);
    }

    const value = {
        currentUser,
        signup,
        login,
        logout,
        resetPassword,
        updateInformation,
        setEmail,
        setPassword,
        reauthenticate,
        deleteAccount
    }

    useEffect(() => {
        const unsubscriber = onAuthStateChanged(auth, (user) => {
            async function initInfo() {
                try {
                    const snapshot = await getDoc(doc(db, "userInfo", user.uid));
                    user.chosenCEFR = 0;
                    user.actualCEFR = 1;
                    user.progressionPoint = 0;
                    user.preferredLang = "vie";
                    if (snapshot.exists()) {
                        const data = snapshot.data();
                        user.progressionPoint = data.progressionPoint ? data.progressionPoint : 0;
                        user.chosenCEFR = data.cefr ? data.cefr : 0;
                        user.actualCEFR = getLevel(user.progressionPoint);
                        user.preferredLang = data.preferredLang ? data.preferredLang : "vie";
                        user.dateJoined = data.dateJoined ? data.dateJoined.toDate() : null;
                        if (data.preferredLang) switchLanguage(data.preferredLang);
                    }
                    setCurrentUser(user);
                    setIsLoading(false);
                } catch (error) {
                    console.log(error);
                }
            }
            if (user) {
                initInfo();
            } else {
                setCurrentUser(user);
                setIsLoading(false);
            }
        });
        return unsubscriber;
    }, []);

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
}